import {getTestProps} from '@benchling/js-test-utils';
import React, {useEffect, useState} from 'react';

import {openScratchPlateChipModal} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateViewer';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import * as DateUtil from 'util/DateUtil';

import {
  WellAttributionEndpoint,
  WellAttributionResponse,
  WellAttributionRow,
} from './ContainerWellAttributionConstants';
import {fetchWellAttribution} from './ContainerWellAttributionDAO';

export interface ContainerWellAttributionTableProps {
  inventoryPlateId: string;
  rowIndex: number;
  columnIndex: number;
}

interface State {
  attribution: WellAttributionResponse | null;
  hasError: boolean;
}

function AmountCell({row}: {row: WellAttributionRow}): JSX.Element {
  return (
    <Cell>
      {row.transferAmount != null
        ? `${row.transferAmount}${row.transferUnit != null ? ` ${row.transferUnit}` : ''}`
        : null}
    </Cell>
  );
}

function AuthParentCell({row}: {row: WellAttributionRow}): JSX.Element {
  if (row.authParentId == null) {
    return <Cell>Unavailable</Cell>;
  }
  return (
    <Cell>
      <BenchChip id={row.authParentId} size={ChipSize.TABLE_CELL} />
    </Cell>
  );
}

function EndpointCell({endpoint}: {endpoint: WellAttributionEndpoint}): JSX.Element {
  if (endpoint.isViewedWell) {
    return <Cell>This well</Cell>;
  }
  if (endpoint.inventoryWellId != null) {
    return (
      <Cell>
        <BenchChip id={endpoint.inventoryWellId} size={ChipSize.TABLE_CELL} />
      </Cell>
    );
  }
  return (
    <Cell>
      <BenchChip
        displayNameOverride={`${endpoint.name}:${endpoint.well}`}
        id={endpoint.plateId}
        onClick={() => {
          openScratchPlateChipModal({
            scratchPlateId: endpoint.plateId,
            plateName: endpoint.name,
          });
        }}
        size={ChipSize.TABLE_CELL}
      />
    </Cell>
  );
}

interface AttributionTableProps {
  emptyTableText: string;
  isLoading: boolean;
  rows: Array<WellAttributionRow>;
  title: string;
}

function AttributionTable({emptyTableText, isLoading, rows, title}: AttributionTableProps): JSX.Element {
  return (
    <section aria-label={title}>
      <h3>{title}</h3>
      <Table
        emptyTableText={isLoading ? 'Loading transfers...' : emptyTableText}
        getRowKey={(rowIndex) => {
          const row = rows[rowIndex];
          return `${row.source.plateId}:${row.source.well}->${row.destination.plateId}:${row.destination.well}`;
        }}
        isLoading={isLoading}
        numRows={rows.length}
      >
        <Column header="Distance" renderCell={(rowIndex) => <Cell>{rows[rowIndex].distance}</Cell>} />
        <Column
          header="Source"
          renderCell={(rowIndex) => <EndpointCell endpoint={rows[rowIndex].source} />}
        />
        <Column
          header="Destination"
          renderCell={(rowIndex) => <EndpointCell endpoint={rows[rowIndex].destination} />}
        />
        <Column header="Amount" renderCell={(rowIndex) => <AmountCell row={rows[rowIndex]} />} />
        <Column
          header="Transferred"
          renderCell={(rowIndex) => (
            <Cell>{DateUtil.readableTime(rows[rowIndex].transferredAt, {showTime: false})}</Cell>
          )}
        />
        <Column header="Link" renderCell={(rowIndex) => <AuthParentCell row={rows[rowIndex]} />} />
      </Table>
    </section>
  );
}

export default function ContainerWellAttributionTable({
  inventoryPlateId,
  rowIndex,
  columnIndex,
}: ContainerWellAttributionTableProps): JSX.Element {
  const [state, setState] = useState<State>({attribution: null, hasError: false});

  useEffect(() => {
    let isActive = true;
    setState({attribution: null, hasError: false});
    fetchWellAttribution(inventoryPlateId, rowIndex, columnIndex).then(
      (attribution) => {
        if (isActive) {
          setState({attribution, hasError: false});
        }
      },
      () => {
        if (isActive) {
          setState({attribution: null, hasError: true});
        }
      }
    );
    return () => {
      isActive = false;
    };
  }, [columnIndex, inventoryPlateId, rowIndex]);

  const isLoading = state.attribution == null && !state.hasError;
  const errorText = 'Unable to load well attribution. Refresh the page to try again.';

  return (
    <div {...getTestProps({component: 'ContainerWellAttributionTable', element: 'root'})}>
      <AttributionTable
        emptyTableText={state.hasError ? errorText : 'No automation template transfers to this well'}
        isLoading={isLoading}
        rows={state.attribution?.transfersTo ?? []}
        title="Automation Template Transfers To"
      />
      <AttributionTable
        emptyTableText={state.hasError ? errorText : 'No automation template transfers from this well'}
        isLoading={isLoading}
        rows={state.attribution?.transfersFrom ?? []}
        title="Automation Template Transfers From"
      />
      {state.attribution?.truncated && (
        <p className="u-faded">
          <strong>Lineage truncated.</strong> Some attribution lineage is not shown.
        </p>
      )}
    </div>
  );
}
